import React from 'react';
import { Box, Chip, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { filesForCategory } from './legislativeTour';
import PartnerLogo from './PartnerLogo';
import { partnerOrgName } from './partnerLogos';
import { locatorFor, skylineFor } from './locationArt';
import { TREND_COLORS } from './maps/mapColorScales';
import useLocationHighlights from './useLocationHighlights';
import LandingStory from './LandingStory';
import { CountUp, Reveal } from './landingMotion';

export const SECTION_COPY = {
  highlights: {
    title: 'At a Glance',
    description: () => 'Headline numbers from the interactive maps. Select any one to explore the map behind it.',
  },
  presentation: {
    title: 'Analytical Presentation',
    description: (name) =>
      `A narrative summary of dynamics impacting ${name}. Start here for the story, then explore deeper through the interactive maps.`,
  },
  maps: {
    title: 'Interactive Maps',
    description: () =>
      'Explore specific dynamics in depth through this series of interactive maps to view area and school-level details.',
  },
  calculator: {
    title: 'Calculators',
    description: () =>
      'Rank districts, planning regions, and schools with live indicator weights.',
  },
  data: {
    title: 'Data Sets',
    description: () =>
      'The spreadsheets behind these maps.',
  },
  documentation: {
    title: 'Data Sources and Documentation',
    description: () => 'Methodology notes and sources behind the maps and analysis.',
  },
};

const RESOURCE_ICONS = {
  presentation: CoPresentOutlinedIcon,
  documentation: MenuBookOutlinedIcon,
  data: TableChartOutlinedIcon,
  calculator: CalculateOutlinedIcon,
};

const isGradeBanded = (category) =>
  Boolean(category?.files?.length) && category.files.every((file) => file.gradeBand);

export const analysisTitle = (name) => {
  const trimmed = String(name || '').trim();
  return trimmed ? `${trimmed} Analysis` : 'Analysis';
};

const mapMeta = (category) => {
  const files = filesForCategory(category);
  if (!files.length) return '';
  if (isGradeBanded(category)) {
    return files.map((file) => file.shortLabel || file.label).join(' · ');
  }
  if (files.length === 1) return files[0].label;
  return `${files.length} views`;
};

const placeName = (location) => String(location?.name || 'this market').trim() || 'this market';

const SectionHeader = ({ title, description }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.5,
      mb: 2,
      pl: 1.5,
      borderLeft: '4px solid',
      borderColor: 'primary.main',
    }}
  >
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark', lineHeight: 1.3 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

const activateOnKey = (onClick) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onClick();
  }
};

const clickableCardSx = {
  p: 2.5,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover, &:focus-visible': {
    borderColor: 'primary.main',
    boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
    transform: 'translateY(-2px)',
    '& .landing-card-title': { color: 'primary.main' },
    '& .landing-card-chevron': { transform: 'translateX(4px)', color: 'primary.main' },
  },
};

const LandingCard = ({ icon: Icon, title, description, meta, onClick }) => (
  <Paper
    elevation={0}
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={activateOnKey(onClick)}
    sx={clickableCardSx}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: description ? 1 : 0 }}>
      <Icon fontSize="small" color="primary" sx={{ mt: '2px' }} />
      <Typography
        className="landing-card-title"
        variant="subtitle1"
        sx={{ fontWeight: 600, lineHeight: 1.3, transition: 'color 0.2s ease-in-out', flexGrow: 1 }}
      >
        {title}
      </Typography>
      <ChevronRightIcon
        className="landing-card-chevron"
        sx={{ color: 'text.disabled', transition: 'all 0.2s ease-in-out', mt: '2px' }}
      />
    </Box>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: meta ? 1.5 : 0 }}>
        {description}
      </Typography>
    )}
    {meta && (
      <Chip
        size="small"
        label={meta}
        variant="outlined"
        sx={{ mt: 'auto', alignSelf: 'flex-start', maxWidth: '100%' }}
      />
    )}
  </Paper>
);

const HIGHLIGHT_TONE_COLORS = {
  count: 'primary.main',
  positive: TREND_COLORS.Growing,
  negative: TREND_COLORS.Declining,
  neutral: 'text.secondary',
};

const HighlightTile = ({ highlight, onClick }) => (
  <Paper
    elevation={0}
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={activateOnKey(onClick)}
    data-testid={`landing-highlight-${highlight.id}`}
    sx={{ ...clickableCardSx, p: 2 }}
  >
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.06em' }}
    >
      {highlight.label}
    </Typography>
    <Typography
      variant="h5"
      component="p"
      sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.25, mb: 0.75, color: HIGHLIGHT_TONE_COLORS[highlight.tone] || HIGHLIGHT_TONE_COLORS.neutral }}
    >
      <CountUp value={highlight.value} />
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      {highlight.text}
    </Typography>
    <Box
      className="landing-card-title"
      sx={{
        mt: 'auto',
        display: 'flex',
        alignItems: 'center',
        color: 'text.primary',
        fontWeight: 600,
        fontSize: '0.8125rem',
        transition: 'color 0.2s ease-in-out',
      }}
    >
      Explore the map
      <ChevronRightIcon
        className="landing-card-chevron"
        fontSize="small"
        sx={{ color: 'text.disabled', transition: 'all 0.2s ease-in-out' }}
      />
    </Box>
  </Paper>
);

const HIGHLIGHT_SKELETON_COUNT = 4;

const AtAGlance = ({ loading, highlights, categories, onSelectCategory }) => {
  if (!loading && !highlights.length) return null;
  const tiles = loading
    ? Array.from({ length: HIGHLIGHT_SKELETON_COUNT }, (_, index) => ({ skeleton: true, id: index }))
    : highlights;
  const columns = Math.min(tiles.length, HIGHLIGHT_SKELETON_COUNT);

  return (
    <Box sx={{ mb: 4 }} data-testid="landing-highlights" aria-busy={loading || undefined}>
      <SectionHeader title={SECTION_COPY.highlights.title} description={SECTION_COPY.highlights.description()} />
      <Grid container spacing={2}>
        {tiles.map((tile, index) => (
          <Grid item xs={12} sm={columns === 1 ? 12 : 6} md={12 / columns} key={tile.id}>
            {tile.skeleton ? (
              <Skeleton variant="rounded" height={168} sx={{ borderRadius: 2 }} />
            ) : (
              <Reveal delay={index * 100} sx={{ height: '100%' }}>
                <HighlightTile
                  highlight={tile}
                  onClick={() => {
                    const category = categories.find((c) => c.id === tile.categoryId);
                    if (category) onSelectCategory(category);
                  }}
                />
              </Reveal>
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const LocatorBanner = ({ locator, onClick }) => (
  <Paper
    elevation={0}
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    }}
    aria-label={locator.alt}
    sx={{
      mb: 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      cursor: 'pointer',
      bgcolor: '#f8fafc',
      height: { xs: 140, md: 200 },
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: 'primary.main',
        boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
      },
    }}
  >
    <Box
      component="img"
      src={locator.src}
      alt=""
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
      }}
    />
  </Paper>
);

const LocationLanding = ({ location, onSelectCategory, onSelectResource, fetchSummary }) => {
  const categories = location?.categories || [];
  const { loading: highlightsLoading, highlights, summary } = useLocationHighlights(location, fetchSummary);
  const resources = location?.resources || [];
  const presentation = resources.find((resource) => resource.kind === 'presentation');
  const documentation = resources.find((resource) => resource.kind === 'documentation');
  const calculatorResources = resources.filter((resource) => resource.kind === 'calculator');
  const dataResources = resources.filter((resource) => resource.kind === 'data');
  const metaLines = [partnerOrgName(location), location?.county].filter(Boolean);
  const hasContent = Boolean(
    presentation || categories.length || calculatorResources.length || documentation || dataResources.length
  );
  const name = placeName(location);
  const skyline = skylineFor(location?.id);
  const locator = locatorFor(location?.id);

  return (
    <Box data-testid="location-landing" sx={{ p: { xs: 2.5, md: 4 }, maxWidth: 960, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 3, md: 3.5 },
          minHeight: { xs: 168, md: 188 },
          mb: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
        }}
      >
        {skyline && (
          <Box
            component="img"
            src={skyline.src}
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              right: { xs: -24, md: 0 },
              bottom: 0,
              height: { xs: '70%', md: '86%' },
              width: 'auto',
              maxWidth: { xs: '68%', md: '58%' },
              objectFit: 'contain',
              objectPosition: 'right bottom',
              opacity: 0.16,
              pointerEvents: 'none',
              maskImage: 'linear-gradient(to right, transparent 0%, black 28%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 28%)',
            }}
          />
        )}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.75rem' }}>
              City Explorer
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {analysisTitle(location?.name)}
            </Typography>
          </Box>
          <PartnerLogo
            locationId={location?.id}
            size="header"
            sx={{ objectPosition: 'right center', flexShrink: 0 }}
          />
        </Box>
        {metaLines.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, position: 'relative' }}>
            {metaLines.join(' · ')}
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ position: 'relative' }}>
          {location?.tagline ||
            'Choose a map or document to explore this market. You can also pick from the panel on the left.'}
        </Typography>
      </Paper>

      <AtAGlance
        loading={highlightsLoading}
        highlights={highlights}
        categories={categories}
        onSelectCategory={onSelectCategory}
      />

      {summary?.schools && (
        <LandingStory location={location} summary={summary} onSelectCategory={onSelectCategory} />
      )}

      {!hasContent && (
        <Typography variant="body2" color="text.secondary">
          This location has no published content yet.
        </Typography>
      )}

      {presentation && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            title={SECTION_COPY.presentation.title}
            description={SECTION_COPY.presentation.description(name)}
          />
          <LandingCard
            icon={RESOURCE_ICONS.presentation}
            title={presentation.label}
            onClick={() => onSelectResource(presentation)}
          />
        </Box>
      )}

      {categories.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader title={SECTION_COPY.maps.title} description={SECTION_COPY.maps.description()} />
          {locator && (
            <LocatorBanner locator={locator} onClick={() => onSelectCategory(categories[0])} />
          )}
          <Grid container spacing={2}>
            {categories.map((category) => (
              <Grid item xs={12} md={6} key={category.id}>
                <LandingCard
                  icon={MapOutlinedIcon}
                  title={category.label}
                  description={category.description}
                  meta={mapMeta(category)}
                  onClick={() => onSelectCategory(category)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {calculatorResources.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            title={SECTION_COPY.calculator.title}
            description={SECTION_COPY.calculator.description()}
          />
          <Stack spacing={2}>
            {calculatorResources.map((resource) => (
              <LandingCard
                key={resource.id}
                icon={RESOURCE_ICONS.calculator}
                title={resource.label}
                onClick={() => onSelectResource(resource)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {dataResources.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader title={SECTION_COPY.data.title} description={SECTION_COPY.data.description()} />
          <Stack spacing={2}>
            {dataResources.map((resource) => (
              <LandingCard
                key={resource.id}
                icon={RESOURCE_ICONS.data}
                title={resource.label}
                onClick={() => onSelectResource(resource)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {documentation && (
        <Box sx={{ mb: 2 }}>
          <SectionHeader
            title={SECTION_COPY.documentation.title}
            description={SECTION_COPY.documentation.description()}
          />
          <LandingCard
            icon={RESOURCE_ICONS.documentation}
            title={documentation.label}
            onClick={() => onSelectResource(documentation)}
          />
        </Box>
      )}
    </Box>
  );
};

export default LocationLanding;
